import React from 'react';

/**
 * Elemento que está tocando agora, na página inteira.
 *
 * Sem esse registro, dois cards tocam ao mesmo tempo: cada `<audio>` é
 * independente e o navegador não coordena nada entre eles. Guardar em módulo
 * (e não num contexto React) é o que faz a regra valer também entre dois
 * blocos de rádio na mesma página.
 */
let tocandoAgora: HTMLAudioElement | null = null;

export interface AudioPlayer {
  audioRef: React.RefObject<HTMLAudioElement>;
  tocando: boolean;
  /** Segundos já tocados. */
  atual: number;
  /** Duração total; `0` enquanto os metadados não chegam. */
  duracao: number;
  alternar: () => void;
  buscar: (segundos: number) => void;
}

/**
 * Estado de um `<audio>` para desenhar o player do card.
 *
 * Os tempos vêm de eventos do próprio elemento (`timeupdate`,
 * `loadedmetadata`) em vez de um `setInterval`: é o navegador que sabe quando
 * o áudio realmente andou, e a barra para junto quando o buffer trava.
 */
export const useAudioPlayer = (): AudioPlayer => {
  const audioRef = React.useRef<HTMLAudioElement>(null);
  const [tocando, setTocando] = React.useState(false);
  const [atual, setAtual] = React.useState(0);
  const [duracao, setDuracao] = React.useState(0);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return undefined;

    const aoAtualizar = () => setAtual(audio.currentTime);
    const aoCarregar = () =>
      setDuracao(Number.isFinite(audio.duration) ? audio.duration : 0);
    const aoTocar = () => setTocando(true);
    const aoPausar = () => setTocando(false);
    const aoTerminar = () => {
      setTocando(false);
      setAtual(0);
      if (tocandoAgora === audio) tocandoAgora = null;
    };

    // Estado inicial lido do elemento, e não só dos eventos: com o arquivo já
    // em cache do navegador, `loadedmetadata` dispara ANTES deste efeito rodar
    // — e o evento não se repete. Sem isto a duração fica presa em 0:00 e o
    // `max` do range fica 0, deixando a barra de progresso inerte. Só aparece
    // com arquivo local rápido; com áudio remoto o evento chega depois e
    // mascara o problema.
    if (audio.readyState >= 1 /* HAVE_METADATA */) {
      aoCarregar();
      setAtual(audio.currentTime);
    }
    if (!audio.paused) setTocando(true);

    audio.addEventListener('timeupdate', aoAtualizar);
    audio.addEventListener('loadedmetadata', aoCarregar);
    audio.addEventListener('durationchange', aoCarregar);
    audio.addEventListener('play', aoTocar);
    audio.addEventListener('pause', aoPausar);
    audio.addEventListener('ended', aoTerminar);

    return () => {
      audio.removeEventListener('timeupdate', aoAtualizar);
      audio.removeEventListener('loadedmetadata', aoCarregar);
      audio.removeEventListener('durationchange', aoCarregar);
      audio.removeEventListener('play', aoTocar);
      audio.removeEventListener('pause', aoPausar);
      audio.removeEventListener('ended', aoTerminar);
      if (tocandoAgora === audio) tocandoAgora = null;
    };
  }, []);

  const alternar = React.useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (audio.paused) {
      if (tocandoAgora && tocandoAgora !== audio) tocandoAgora.pause();
      tocandoAgora = audio;
      // `play()` devolve promessa e rejeita quando o navegador bloqueia o
      // autoplay; sem o `catch` isso vira "unhandled rejection" no console.
      void audio.play().catch(() => setTocando(false));
    } else {
      audio.pause();
    }
  }, []);

  const buscar = React.useCallback((segundos: number) => {
    const audio = audioRef.current;
    if (!audio || !Number.isFinite(segundos)) return;
    audio.currentTime = segundos;
    setAtual(segundos);
  }, []);

  return { audioRef, tocando, atual, duracao, alternar, buscar };
};

export default useAudioPlayer;
